// src/app/components/admin/truth-set-automation-setting.tsx
//
// Platform section of Global Settings: the off switch and spend caps for the
// truth set's unattended steps (scheduled site reads, fact extraction after a
// site read, the answer check after a read). Hand-launched runs are not
// affected. Backend: app/services/truth_set/automation_config.py.

import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import {
  getTruthAutomation, saveTruthAutomation, type TruthAutomationSettings, type TruthAutomationView,
} from "@/api/truth-set";

const muted = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 } as const;
const field = {
  fontSize: "var(--text-sm)", height: 32, padding: "0 10px", borderRadius: 8, width: 120,
  border: "1px solid var(--border)", backgroundColor: "var(--background)", color: "var(--foreground)",
} as const;

const FIELDS: { key: Exclude<keyof TruthAutomationSettings, "enabled">; label: string; hint: string }[] = [
  { key: "run_cap_usd", label: "Spend cap per automatic run", hint: "Each automatically launched extraction or answer check stops at this spend." },
  { key: "monthly_cap_usd", label: "Monthly cap, all clients", hint: "Automatic runs launch only while this month's truth set spend, plus what running work may still spend, plus one run cap, stays under it. Hand-launched runs count toward it but are not refused." },
];

const usd = (n: number) => `$${n.toFixed(2)}`;

export function TruthSetAutomationSetting() {
  const [view, setView] = useState<TruthAutomationView | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const adopt = (v: TruthAutomationView) => {
    setView(v);
    setDraft(Object.fromEntries(FIELDS.map((f) => [f.key, String(v.settings[f.key])])));
  };

  useEffect(() => {
    getTruthAutomation().then(adopt).catch((e) => setErr(String(e?.message || e)));
  }, []);

  async function save(values: Partial<TruthAutomationSettings>) {
    setSaving(true);
    setErr("");
    setSaved(false);
    try {
      adopt(await saveTruthAutomation(values));
      setSaved(true);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  const settings = view?.settings;
  const changed = settings != null && FIELDS.some((f) => draft[f.key] !== String(settings[f.key]));

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Truth set automation
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          Three truth set steps run without anyone pressing a button: the scheduled site re-read, fact extraction
          after a site read, and the answer check after every read. Switched off, none of them launches; runs
          already going finish, and anything an admin launches by hand still runs. A read that finishes while
          this is off is not checked later by itself.
        </p>

        {err && (
          <div className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}>
            {err}
          </div>
        )}
        {!view && !err && (
          <p className="mt-5 inline-flex items-center gap-2" style={muted}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </p>
        )}

        {settings && (
          <div className="mt-5 flex flex-col gap-4">
            <label className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
              <input type="checkbox" checked={settings.enabled} disabled={saving}
                onChange={(e) => save({ enabled: e.target.checked })} />
              Automatic truth set steps are {settings.enabled ? "on" : "off"}
            </label>
            <p style={muted}>
              {view?.month
                ? `This month: ${usd(view.month.spent)} spent, ${usd(view.month.committed)} more committed to running work, of a ${usd(settings.monthly_cap_usd)} cap.`
                : "This month's spend could not be read; automatic paid steps will not launch until it can."}
            </p>
            {FIELDS.map((f) => (
              <div key={f.key} className="flex flex-wrap items-center gap-3">
                <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", minWidth: 220 }}>{f.label}</span>
                <input inputMode="decimal" style={field} value={draft[f.key] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
                <span style={muted}>USD</span>
                <span style={{ ...muted, flexBasis: "100%" }}>{f.hint}</span>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button type="button" disabled={!changed || saving}
                onClick={() => save(Object.fromEntries(FIELDS.map((f) => [f.key, Number(draft[f.key])])) as Partial<TruthAutomationSettings>)}
                className="rounded-lg border px-3 py-2"
                style={{ fontSize: "var(--text-sm)", cursor: changed && !saving ? "pointer" : "default",
                         borderColor: "var(--border)", opacity: changed ? 1 : 0.6 }}>
                {saving ? "Saving…" : "Save limits"}
              </button>
              {saved && (
                <span className="inline-flex items-center gap-1" style={{ ...muted, color: "var(--alpha-teal)" }}>
                  <Check className="h-3.5 w-3.5" /> Saved
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
